import React, { useEffect, useState } from 'react';
import { Redirect, Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { PageHero, LoadingState, Section, RichText, EmptyState } from '../Common/PageLayout';
import NotFound from '../Common/NotFound';
import client from '../Contentful_API/api';
import MinistryMessagePage from './MinistryMessagePage';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
  article: {
    backgroundColor: tokens.white,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radius,
    boxShadow: tokens.shadowSm,
    padding: theme.spacing(5),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(3) },
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: tokens.radius },
    '& table': { width: '100%', borderCollapse: 'collapse', display: 'block', overflowX: 'auto' },
    '& iframe': { maxWidth: '100%' },
  },
  aside: {
    position: 'sticky',
    top: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: { position: 'static' },
  },
  card: {
    backgroundColor: tokens.white,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radius,
    boxShadow: tokens.shadowSm,
    overflow: 'hidden',
  },
  imageLink: {
    display: 'block',
    aspectRatio: '4 / 3',
    backgroundColor: tokens.blue50,
    overflow: 'hidden',
    '&:hover img, &:focus-visible img': { transform: 'scale(1.03)' },
  },
  image: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  cardBody: { padding: theme.spacing(2, 2.5) },
  cardLabel: {
    display: 'block',
    color: tokens.blue600,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.5),
  },
  cardTitle: {
    fontFamily: tokens.headingFont,
    fontWeight: 600,
    fontSize: '0.9375rem',
    color: tokens.ink,
    margin: 0,
  },
  cardLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: theme.spacing(1.5),
    color: tokens.blue600,
    fontWeight: 600,
    fontSize: '0.875rem',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  helpCard: {
    backgroundColor: tokens.blue50,
    border: `1px solid ${tokens.blue100}`,
    borderRadius: tokens.radius,
    padding: theme.spacing(2.5),
  },
  helpText: {
    color: tokens.inkSoft,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    margin: `${theme.spacing(1)}px 0 0`,
  },
}));

function getMediaUrl(media) {
  if (!media) return '';
  return media.fields?.file?.url || media.url || media.formats?.medium?.url || media.formats?.small?.url || '';
}

function getRelationFields(relation) {
  return relation?.fields || relation?.attributes || relation || {};
}

function isMinistryMessage(page) {
  const subMenu = getRelationFields(page.sub_menu || page.subMenu);
  const category = page.pageCategory || page.category || page.pageType || page.type || page.template
    || subMenu.pageCategory || subMenu.category || subMenu.pageType || subMenu.type || subMenu.template
    || subMenu.subMName || page.title;
  return typeof category === 'string' && /^(callout|ministry message|minister(?:'s|s)? message|the minister|minister)$/i.test(category.trim());
}

async function getPageBySlug(slug) {
  try {
    const pageResponse = await client.getEntries({
      content_type: 'collectionpagedetail',
      'fields.sub_menu.slugMName': slug,
    });
    if (pageResponse.items?.[0]) return pageResponse.items[0].fields || {};

    const alternateResponse = await client.getEntries({
      content_type: 'collectionpagedetail',
      'fields.sub_menu.slug': slug,
    });
    if (alternateResponse.items?.[0]) return alternateResponse.items[0].fields || {};
  } catch (error) {
    console.warn('Collection page lookup failed; trying legacy menus.', error);
  }

  const legacyResponse = await client.getEntries({ content_type: 'menus', 'fields.slug': slug });
  return legacyResponse.items?.[0]?.fields || null;
}

export default function DynamicPage({ location, match }) {
  const classes = useStyles();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const slug = match?.params?.slug || location.pathname.replace(/^\/+|\/+$/g, '');

  useEffect(() => {
    let active = true;
    setPage(null);
    setNotFound(false);

    getPageBySlug(slug)
      .then((fields) => {
        if (!active) return;
        if (fields) setPage(fields);
        else setNotFound(true);
      })
      .catch(() => { if (active) setNotFound(true); });

    return () => { active = false; };
  }, [slug]);

  if (!page && !notFound) return <LoadingState label="Loading page" />;
  if (notFound && slug === 'minister-of-finance') return <Redirect to="/profile" />;
  if (notFound) return <NotFound />;

  const title = page.title || 'Ministry of Finance';
  const imageUrl = getMediaUrl(page.cover_page || page.mediafile || page.image || page.media);

  if (isMinistryMessage(page)) {
    const subMenu = getRelationFields(page.sub_menu || page.subMenu);
    const ministryMessagePage = {
      ...subMenu,
      ...page,
      title: page.title || subMenu.title || subMenu.subMName,
      description: page.description || subMenu.description || subMenu.content,
    };
    return <MinistryMessagePage page={ministryMessagePage} />;
  }

  return (
    <>
      <PageHero title={title} breadcrumbs={[{ label: title }]} />
      <Section tone="muted">
        <Grid container spacing={4}>
          <Grid item xs={12} md={imageUrl ? 8 : 12}>
            <article className={classes.article}>
              {page.description ? (
                <RichText html={page.description} />
              ) : (
                <EmptyState title="No content yet" description="This page has no published content." />
              )}
            </article>
          </Grid>
          {imageUrl && (
            <Grid item xs={12} md={4} component="aside">
              <div className={classes.aside}>
                <div className={classes.card}>
                  <a
                    href={imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={classes.imageLink}
                    aria-label={`Open full image: ${title}`}
                  >
                    <img src={imageUrl} alt={title} className={classes.image} loading="lazy" />
                  </a>
                  <div className={classes.cardBody}>
                    <span className={classes.cardLabel}>Featured</span>
                    <p className={classes.cardTitle}>{title}</p>
                    <a href={imageUrl} target="_blank" rel="noreferrer" className={classes.cardLink}>
                      View full image <OpenInNewIcon style={{ fontSize: 16 }} />
                    </a>
                  </div>
                </div>
                <div className={classes.helpCard}>
                  <p className={classes.cardTitle}>Need more information?</p>
                  <p className={classes.helpText}>
                    Contact the Ministry of Finance for questions about this page.
                  </p>
                  <RouterLink to="/contact" className={classes.cardLink}>
                    <MailOutlineIcon style={{ fontSize: 16 }} /> Contact us
                  </RouterLink>
                </div>
              </div>
            </Grid>
          )}
        </Grid>
      </Section>
    </>
  );
}
